// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Stack, useRouter } from 'expo-router';
import * as Application from 'expo-application';
import Constants from 'expo-constants';
import { FlatList, Linking, Modal, Platform, Pressable, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
// Relative, not the `@/assets/*` alias — that alias points at assets-generated/ (S3-synced
// media), while licenses.json lives in the real ./assets/ folder alongside fonts and icons.
import licenses from '../assets/licenses.json';

type LicenseEntry = {
  name: string;
  version: string;
  licenses: string;
  repository: string | null;
  licenseText: string | null;
};

const LICENSE_LIST = licenses as LicenseEntry[];

// These licenses place no obligations on a redistributor — no attribution notice to
// reproduce — so listing them here would just be noise. A package under an OR-choice of
// licenses (e.g. "MIT OR CC0-1.0") can always be redistributed under the no-obligation
// option, so it is skipped too.
const NO_ATTRIBUTION_LICENSES = new Set(['Unlicense', 'CC0-1.0', '0BSD', 'Public Domain']);

function requiresMention(licenseId: string): boolean {
  const options = licenseId
    .replace(/[()]/g, '')
    .split(/\s+OR\s+/i)
    .map((option) => option.trim());
  return !options.some((option) => NO_ATTRIBUTION_LICENSES.has(option));
}

const MENTIONABLE_LICENSES = LICENSE_LIST.filter((entry) => requiresMention(entry.licenses));

export default function AboutScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const [licensesOpen, setLicensesOpen] = useState(false);
  const [selected, setSelected] = useState<LicenseEntry | null>(null);

  // The installed app's own numbers first: releases set the versionCode at build time
  // (android-release-internal.sh), so app.json's value is not what is on the phone. Web has no
  // native build and falls back to the config.
  const appVersion = Application.nativeApplicationVersion ?? Constants.expoConfig?.version ?? 'unknown';
  const buildNumber =
    Application.nativeBuildVersion ??
    (Platform.OS === 'android'
      ? Constants.expoConfig?.android?.versionCode
      : Constants.expoConfig?.ios?.buildNumber);
  const gitCommit = (Constants.expoConfig?.extra?.gitCommit as string | undefined) ?? 'unknown';
  const buildDate = Constants.expoConfig?.extra?.buildDate as string | undefined;

  const renderLicenseRow = ({ item }: { item: LicenseEntry }) => (
    <Pressable style={styles.licenseRow} onPress={() => setSelected(item)}>
      <Text style={styles.licenseName}>{item.name}</Text>
      <Text style={styles.licenseMeta}>
        {item.version} · {item.licenses}
      </Text>
    </Pressable>
  );

  return (
    <>
      <Stack.Screen options={{ title: t('about.title') }} />
      <View style={styles.container}>
        <View style={styles.contentContainer}>
          {/* eslint-disable-next-line i18n/no-literal-text -- the product name is not translated */}
          <Text style={styles.appName}>Goffy</Text>
          <View style={styles.infoBlock}>
            <InfoRow label={t('about.version')} value={appVersion} />
            {buildNumber != null ? <InfoRow label={t('about.build')} value={String(buildNumber)} /> : null}
            <InfoRow label={t('about.commit')} value={gitCommit} />
            {buildDate ? (
              <InfoRow label={t('about.buildDate')} value={new Date(buildDate).toLocaleString()} />
            ) : null}
          </View>

          <Pressable style={styles.linkRow} onPress={() => router.push('/privacy')}>
            <Text style={styles.linkRowText}>{t('menu.privacy')}</Text>
          </Pressable>
          <Pressable style={styles.linkRow} onPress={() => router.push('/terms')}>
            <Text style={styles.linkRowText}>{t('menu.terms')}</Text>
          </Pressable>

          <Pressable style={styles.linkRow} onPress={() => setLicensesOpen(true)}>
            <Text style={styles.heading}>{t('about.licensesHeading')}</Text>
            <Text style={styles.licensesIntro}>
              {t('about.licensesIntro', { count: MENTIONABLE_LICENSES.length })}
            </Text>
          </Pressable>
        </View>
      </View>

      <Modal visible={licensesOpen} animationType="slide" onRequestClose={() => setLicensesOpen(false)}>
        <View style={styles.container}>
          <FlatList
            style={styles.container}
            contentContainerStyle={styles.contentContainer}
            ListHeaderComponent={<Text style={styles.heading}>{t('about.licensesHeading')}</Text>}
            data={MENTIONABLE_LICENSES}
            keyExtractor={(item) => `${item.name}@${item.version}`}
            renderItem={renderLicenseRow}
            initialNumToRender={20}
            windowSize={10}
          />
          <Pressable style={styles.closeButton} onPress={() => setLicensesOpen(false)}>
            <Text style={styles.closeButtonText}>{t('about.close')}</Text>
          </Pressable>
        </View>
      </Modal>

      <Modal visible={!!selected} transparent animationType="fade" onRequestClose={() => setSelected(null)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setSelected(null)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>{selected?.name}</Text>
            <Text style={styles.modalSubtitle}>
              {selected?.version} · {selected?.licenses}
            </Text>
            {selected?.repository ? (
              <Pressable onPress={() => Linking.openURL(selected.repository as string)}>
                <Text style={[styles.modalSubtitle, styles.link]}>{selected.repository}</Text>
              </Pressable>
            ) : null}
            <FlatList
              style={styles.modalScroll}
              data={[selected?.licenseText ?? t('about.licenseTextUnavailable')]}
              keyExtractor={(_, index) => String(index)}
              renderItem={({ item }) => <Text style={styles.licenseText}>{item}</Text>}
            />
            <Pressable style={styles.closeButton} onPress={() => setSelected(null)}>
              <Text style={styles.closeButtonText}>{t('about.close')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue} selectable>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 48,
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  appName: {
    fontSize: 26,
    fontWeight: '700',
  },
  infoBlock: {
    marginTop: 16,
    marginBottom: 24,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  infoLabel: {
    fontSize: 15,
    opacity: 0.7,
  },
  infoValue: {
    fontSize: 15,
    fontWeight: '600',
  },
  linkRow: {
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: appColors.border.strong,
  },
  linkRowText: {
    fontSize: 15,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  heading: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  licensesIntro: {
    fontSize: 14,
    opacity: 0.7,
    marginBottom: 12,
  },
  licenseRow: {
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: appColors.border.strong,
  },
  licenseName: {
    fontSize: 15,
    fontWeight: '600',
  },
  licenseMeta: {
    fontSize: 13,
    opacity: 0.6,
    marginTop: 2,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: appColors.accent.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalCard: {
    backgroundColor: appColors.surface.base,
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 560,
    maxHeight: '80%',
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  modalSubtitle: {
    fontSize: 13,
    color: appColors.text.secondary,
    marginTop: 2,
  },
  link: {
    textDecorationLine: 'underline',
  },
  modalScroll: {
    marginTop: 12,
  },
  licenseText: {
    fontSize: 12,
    lineHeight: 18,
    color: appColors.text.secondary,
    fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
  },
  closeButton: {
    marginTop: 16,
    alignSelf: 'flex-end',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  closeButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: appColors.text.primary,
  },
});
